import { Button, Tooltip } from 'components/ui';
import {
  AtlasGqlInspectionTask,
  AtlasGqlTask,
  AtlasGqlWorkContainerTypeOption,
} from 'types/atlas-graphql';
import { useMemo, useState } from 'react';
import {
  InitialStep,
  Steps,
  UploadInspectionDataModal,
  UploadTypes,
} from 'horizon/routes/Inspections/InspectionsUploadManager/UploadInspectionDataModal';

type UploadInspectionsToWorkOrderProps = {
  allTasks: AtlasGqlTask[];
  selected: AtlasGqlTask[];
};

export const UploadInspectionsToWorkOrder: React.FC<UploadInspectionsToWorkOrderProps> = ({
  allTasks,
  selected,
}) => {
  const [inspectionUploadModalOpen, setInspectionUploadModalOpen] = useState<boolean>(false);

  const {
    disabled,
    tooltip,
    initialStep,
    inspectionTasks = [],
    existingInspectionIds,
  } = useMemo(() => {
    if (selected.some(({ type }) => type.type !== AtlasGqlWorkContainerTypeOption.InspectionTask)) {
      return {
        disabled: true,
        tooltip: 'Cannot upload Inspections for Non-Inspection Tasks',
        initialStep: Steps.SummaryUpload,
      };
    }

    if (
      !selected.length &&
      !(allTasks as AtlasGqlTask[]).some(
        task => task.type.type === AtlasGqlWorkContainerTypeOption.InspectionTask
      )
    ) {
      return {
        disabled: true,
        tooltip: 'There are no Inspection Tasks on this Work Order',
        initialStep: Steps.SummaryUpload,
      };
    }

    const tasks = selected.length ? selected : allTasks;
    const _inspectionTasks = tasks.filter(
      task => task.type.type === AtlasGqlWorkContainerTypeOption.InspectionTask
    ) as AtlasGqlInspectionTask[];
    const idsOfTasksWithoutInspections = _inspectionTasks
      .filter(({ inspection }) => !inspection)
      .map(({ id }) => id);
    const inspectionTasks = _inspectionTasks.map(({ id, inspection }) => ({
      taskId: id,
      inspectionId: inspection?.id,
    }));
    const existingInspectionIds = inspectionTasks
      .map(({ inspectionId }) => inspectionId)
      .filter((inspectionId): inspectionId is string => !!inspectionId);

    let initialStep = Steps.SummaryUpload;

    if (!idsOfTasksWithoutInspections.length) {
      initialStep = Steps.DetailsUpload;
    }

    return {
      disabled: !idsOfTasksWithoutInspections.length && !inspectionTasks.length,
      inspectionTasks,
      existingInspectionIds,
      initialStep,
    };
  }, [selected, allTasks]);

  return (
    <>
      <Tooltip title={disabled ? tooltip : ''}>
        <div>
          <Button
            data-testid="wo-inspection-upload-button"
            onClick={() => setInspectionUploadModalOpen(true)}
            disabled={disabled}
          >
            Upload Inspections
          </Button>
        </div>
      </Tooltip>
      <UploadInspectionDataModal
        open={inspectionUploadModalOpen}
        onClose={() => setInspectionUploadModalOpen(false)}
        lockedUploadType={UploadTypes.Multiple}
        initialStep={initialStep as InitialStep}
        requireTaskId={true}
        inspectionTasks={inspectionTasks}
        existingInspectionIds={existingInspectionIds}
      />
    </>
  );
};
